import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, X, Flame, Beef, Wheat, Droplet, Sparkles, ScanSearch, Wine, Eye, ChefHat, ChevronRight, AlertTriangle, Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { compressImage } from "@/lib/mbImage";
import { isNight, getDietaryWarnings } from "@/lib/mbNutrition";
import { addMeal } from "@/lib/mbStorage";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MacroPill = ({ Icon, label, value, unit, color }) => (
  <div className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
    <Icon size={16} style={{ color }} />
    <span className="text-sm font-bold text-slate-800">{value}{unit}</span>
    <span className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
  </div>
);

export const Scanner = ({ profile, onMealAdded, onOpenChef, fromInstagram, onDismissWelcome }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const night = isNight();
  const allergens = result
    ? getDietaryWarnings(profile, [result.dish_name, result.drink_category, ...(result.ingredients || [])])
    : [];

  const reset = () => {
    setPreview(null);
    setResult(null);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPicker = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResult(null);
    try {
      const dataUrl = await compressImage(file, 800, 0.7);
      setPreview(dataUrl);
      // Analisi automatica subito dopo lo scatto/caricamento
      analyze(dataUrl);
    } catch {
      toast.error("Impossibile leggere l'immagine");
    }
  };

  const analyze = async (imageArg) => {
    const image = imageArg || preview;
    if (!image || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const clean = image.split(",")[1];
      const { data } = await axios.post(`${API}/analyze-meal`, {
        image_base64: clean,
        goal: profile.goal,
      });
      setResult(data);
      setSheetOpen(true);
      // Calcola l'avviso una sola volta al salvataggio e memorizzalo sul pasto,
      // così il Diario sa sempre se il pasto è a rischio senza ricalcolare.
      const warnings = getDietaryWarnings(profile, [data.dish_name, data.drink_category, ...(data.ingredients || [])]);
      addMeal({ ...data, image, warnings, warning: warnings.length > 0 });
      onMealAdded?.();
      // Evento Meta "Lead" SOLO alla prima scansione del pasto.
      try {
        if (!localStorage.getItem("mb_lead_tracked")) {
          if (typeof window !== "undefined" && window.fbq) window.fbq("track", "Lead");
          localStorage.setItem("mb_lead_tracked", "1");
        }
      } catch { /* localStorage/pixel non disponibile */ }
      toast.success("Aggiunto al diario", { description: data.dish_name });
      // L'avviso arancione appare DOPO (quindi sopra) il messaggio verde di salvataggio.
      if (warnings.length > 0) {
        toast.warning("Attenzione al tuo profilo", {
          description: warnings.join(" · "),
          icon: <AlertTriangle size={18} />,
          duration: 7000,
        });
      }
      if (data.is_drink && night) {
        toast.warning("Attenzione", { description: "Non perdere di vista il tuo drink", duration: 6000 });
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      toast.error("Analisi non riuscita", {
        description: err?.response?.data?.detail || "Riprova con un'altra foto",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col items-center" data-testid="screen-scanner">
      {fromInstagram && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full mb-4 rounded-2xl p-3.5 flex items-center gap-3 text-white shadow-lg shadow-fuchsia-500/20"
          style={{ background: "linear-gradient(90deg, #833ab4 0%, #fd1d1d 55%, #fcb045 100%)" }}
          data-testid="instagram-welcome-banner"
        >
          <Instagram size={22} className="shrink-0" />
          <div className="flex-1 min-w-0 text-left">
            <p className="font-display text-sm font-bold leading-tight">Benvenuto da Instagram! 👋</p>
            <p className="text-[11px] text-white/90 leading-snug">Fotografa il tuo pasto e scopri calorie e allergeni in un attimo.</p>
          </div>
          <button
            onClick={onDismissWelcome}
            data-testid="dismiss-instagram-welcome"
            aria-label="Chiudi messaggio di benvenuto"
            className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center active:scale-90 transition-transform shrink-0"
          >
            <X size={14} />
          </button>
        </motion.div>
      )}
      <div className="text-center mb-7 mt-2">
        <h1 className="font-display text-3xl font-extrabold text-[#2b90ef] tracking-tight">mangiaBene</h1>
        <p className="text-slate-400 text-xs mt-1 tracking-wide">Intelligenza Nutrizionale</p>
      </div>

      <div className="w-full bg-white rounded-3xl p-5 shadow-[0_8px_24px_rgba(43,144,239,0.08)] border border-slate-100 mb-4">
        <h2 className="font-display text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Sparkles size={16} className="text-[#2cd4bf]" /> Scansiona con l'IA
        </h2>

        <div className="relative w-full h-40 rounded-2xl overflow-hidden mb-4 bg-slate-100 flex items-center justify-center">
          {preview ? (
            <>
              <img src={preview} alt="Pasto" className="w-full h-full object-cover" data-testid="meal-preview" />
              {loading && <div className="mb-scanline" />}
              {loading && <div className="absolute inset-0 bg-slate-900/20" />}
              {!loading && (
                <button
                  onClick={reset}
                  data-testid="clear-photo-btn"
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center active:scale-90 transition-transform"
                >
                  <X size={14} />
                </button>
              )}
            </>
          ) : (
            <Camera size={38} className="text-slate-300" strokeWidth={1.5} />
          )}
        </div>

        {!preview && (
          <button
            type="button"
            onClick={openPicker}
            data-testid="upload-label"
            className="w-full flex items-center justify-center gap-2 cursor-pointer py-3.5 bg-[#2b90ef] hover:bg-[#1d78cc] text-white rounded-xl font-semibold text-sm transition-colors active:scale-[0.98]"
          >
            <Camera size={18} /> Fai una Foto al Pasto
          </button>
        )}

        {preview && (
          <button
            onClick={() => analyze()}
            disabled={loading}
            data-testid="analyze-btn"
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#2b90ef] hover:bg-[#1d78cc] disabled:opacity-70 text-white rounded-xl font-semibold text-sm transition-colors active:scale-[0.98]"
          >
            <ScanSearch size={18} /> {loading ? "Analisi in corso..." : result ? "Analizza di nuovo" : "Analizza Pasto"}
          </button>
        )}

        <input
          id="mb-upload"
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFile}
          data-testid="upload-input"
        />
      </div>

      {/* Chef Oggi (nascosto durante la scansione) */}
      {!preview && !result && (
      <button
        onClick={onOpenChef}
        data-testid="open-chef-btn"
        className="w-full mb-4 flex items-center gap-2.5 py-2.5 px-3 rounded-2xl bg-[#eef5f1] border border-[#d8e8df] hover:bg-[#e6f1ea] active:scale-[0.98] transition-colors"
      >
        <span className="w-8 h-8 rounded-xl bg-[#d6ebdf] flex items-center justify-center shrink-0">
          <ChefHat size={17} className="text-[#4f9e78]" strokeWidth={2} />
        </span>
        <span className="text-left flex-1 min-w-0">
          <span className="block font-display text-sm font-bold text-slate-700 leading-tight">Chef Oggi</span>
          <span className="block text-[11px] text-slate-400 truncate">Fotografa il frigo → 2 ricette</span>
        </span>
        <ChevronRight size={16} className="text-[#8bc0a3] shrink-0" />
      </button>
      )}

      {loading && (
        <div className="w-full p-5 bg-white rounded-3xl border border-sky-100 shadow-[0_10px_25px_rgba(43,144,239,0.06)]" data-testid="skeleton-loader">
          <div className="text-sm text-[#2b90ef] font-semibold mb-3 text-center flex items-center justify-center gap-2">
            <Sparkles size={14} className="animate-pulse" /> mangiaBene sta analizzando il piatto...
          </div>
          <div className="h-4 mb-skeleton rounded-md mb-2.5" />
          <div className="h-4 mb-skeleton rounded-md mb-2.5 w-4/5" />
          <div className="h-4 mb-skeleton rounded-md w-1/2" />
        </div>
      )}

      {result && !loading && !sheetOpen && (
        <button
          onClick={() => setSheetOpen(true)}
          data-testid="reopen-result-btn"
          className="text-xs font-semibold text-[#2b90ef] underline"
        >
          Rivedi l'analisi del pasto
        </button>
      )}

      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerContent className="max-w-[400px] mx-auto" data-testid="analysis-sheet">
          {result && (
            <div className="px-5 pb-6 pt-2 max-h-[80vh] overflow-y-auto" data-testid="analysis-result">
              {allergens.length > 0 && (
                <div className="rounded-2xl bg-[#f59e0b] p-4 flex items-start gap-3 mb-4 shadow-lg shadow-amber-500/25" data-testid="scanner-allergy-alert">
                  <AlertTriangle size={24} className="text-white shrink-0 mt-0.5" />
                  <div className="text-[13px] leading-relaxed text-white">
                    <p className="font-extrabold text-sm mb-1 uppercase tracking-wide">Attenzione!</p>
                    <ul className="space-y-0.5 font-semibold">
                      {allergens.map((w, i) => (
                        <li key={`warn-${i}`}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {result.is_drink ? (
                <div className="drink-analysis-card mb-4" data-testid="drink-analysis-card">
                  {night && (
                    <div className="drink-night-alert rounded-lg px-3 py-2 mb-3 flex items-center gap-2 text-[12px] font-semibold relative z-10" data-testid="drink-night-alert">
                      <Eye size={15} /> Non perdere di vista il tuo drink
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3 relative z-10">
                    <div className="text-left">
                      <span className="text-[10px] uppercase tracking-wide text-[#7fdccb] font-bold flex items-center gap-1">
                        <Wine size={12} /> {result.drink_category || "Bevanda"}
                      </span>
                      <DrawerTitle className="font-display text-xl font-bold leading-tight text-white" data-testid="result-dish-name">
                        {result.dish_name}
                      </DrawerTitle>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 font-bold text-2xl font-display text-white" data-testid="result-calories">
                        <Flame size={20} className="text-[#f59e0b]" />{result.calories}
                      </div>
                      <span className="text-[10px] text-white/60">kcal</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 relative z-10">
                    <div className="dac-chip flex-1 rounded-xl py-2.5 text-center">
                      <span className="block text-lg font-bold font-display" data-testid="drink-alcohol-g">{result.alcohol_g}g</span>
                      <span className="text-[9px] uppercase tracking-wide text-white/70">Alcol puro</span>
                    </div>
                    <div className="dac-chip flex-1 rounded-xl py-2.5 text-center">
                      <span className="block text-lg font-bold font-display">{result.carbs}g</span>
                      <span className="text-[9px] uppercase tracking-wide text-white/70">Zuccheri</span>
                    </div>
                    <div className="dac-chip flex-1 rounded-xl py-2.5 text-center">
                      <span className="block text-lg font-bold font-display">{Math.round(result.alcohol_g * 7)}</span>
                      <span className="text-[9px] uppercase tracking-wide text-white/70">kcal vuote</span>
                    </div>
                  </div>
                  {result.tip && (
                    <div className="relative z-10 text-[11px] text-white/90 bg-white/10 border border-white/15 rounded-md px-3 py-2.5 leading-relaxed mt-3">
                      💡 {result.tip}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DrawerHeader className="p-0 mb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-left">
                        <span className="text-[10px] uppercase tracking-wide text-[#2cd4bf] font-bold">{result.meal_type}</span>
                        <DrawerTitle className="font-display text-xl font-bold text-slate-800 leading-tight" data-testid="result-dish-name">
                          {result.dish_name}
                        </DrawerTitle>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="flex items-center gap-1 text-[#f59e0b] font-bold text-2xl font-display" data-testid="result-calories">
                          <Flame size={20} />{result.calories}
                        </div>
                        <span className="text-[10px] text-slate-400">kcal</span>
                      </div>
                    </div>
                  </DrawerHeader>

                  <div className="flex gap-2 mb-4">
                    <MacroPill Icon={Beef} label="Prot" value={result.protein} unit="g" color="#ef4444" />
                    <MacroPill Icon={Wheat} label="Carbo" value={result.carbs} unit="g" color="#f59e0b" />
                    <MacroPill Icon={Droplet} label="Grassi" value={result.fat} unit="g" color="#3b82f6" />
                  </div>

                  {result.ingredients?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {result.ingredients.map((ing, i) => (
                        <span key={`${ing}-${i}`} className="text-[11px] bg-slate-50 border border-slate-100 text-slate-600 px-2.5 py-1 rounded-lg">{ing}</span>
                      ))}
                    </div>
                  )}

                  {result.tip && (
                    <div className="text-xs text-[#78350f] bg-amber-50 border-l-[3px] border-[#f59e0b] rounded-md px-3 py-2.5 leading-relaxed mb-4">
                      💡 {result.tip}
                    </div>
                  )}
                </>
              )}

              <button
                onClick={() => { setSheetOpen(false); reset(); }}
                data-testid="sheet-done-btn"
                className="sticky bottom-0 w-full py-3 bg-[#2cd4bf] text-white rounded-xl font-semibold text-sm active:scale-[0.98] transition-transform shadow-[0_-4px_12px_rgba(255,255,255,0.9)]"
              >
                Fatto · Salvato nel diario
              </button>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
};
